import { mutateMoldStocks } from "@/api/moldStock/useMoldStockQuery";
import { useModal } from "@/features/modal/ModalStackManager";
import { MoldDetailForm } from "@/features/mold/form/moldDetailForm";
import { MoldStockDetailForm } from "@/features/moldStock/form/moldStockDeatailForm";
import { MoldStockCreateForm } from "@/features/moldStock/form/stockCreateForm";
import { MoldStockUpdateForm } from "@/features/moldStock/form/stockUpdateForm";
import { useStandardLayout } from "@/features/standardLayout/Context";
import { Filter } from "@/features/standardLayout/Filter";
import { LinkAnchor } from "@/features/standardLayout/LinkAnchor";
import { ListTable } from "@/features/standardLayout/ListTable";
import { PaginationBox } from "@/features/standardLayout/PaginationBox";
import { SearchBox } from "@/features/standardLayout/SearchBox";
import { Sorter } from "@/features/standardLayout/Sorter";
import { ActionHeader } from "@/features/standardLayout/StandardInfoActionHeader";
import { flexGrowable, flexStable, StandardLayoutTableWrapper } from "@/features/standardLayout/Styles";
import customAlert from "@/features/ui/alert/alert";
import { ConfirmForm } from "@/features/ui/confirm";
import { handleErrorResponse } from "@/utils/errorMessage";
import { ActionIcon, Avatar, Flex, Text, Tooltip } from "@mantine/core";
import { MasterApiMoldStocksMoldStockIdDeleteRequest, MoldStocksGet200Response, MoldStocksGet200ResponseRowsInner } from "@sizlcorp/sizl-api-document/dist/models";
import { IconEdit, IconFilterPlus, IconTrash } from "@tabler/icons-react";
import { useMutation } from "@tanstack/react-query";
import { useMemo } from "react";
import { Column, SelectColumn } from "react-data-grid";



//금형 재고 / 실 금형

export const MoldStockPage = () => {
  const { openModal, closeModal } = useModal();
  const { selectedRows, refetch } = useStandardLayout();
  const { mutate: deleteMutate } = useMutation(
    (params: MasterApiMoldStocksMoldStockIdDeleteRequest) =>
      mutateMoldStocks.delete(params).mutationFn(params as MasterApiMoldStocksMoldStockIdDeleteRequest | any),
    {
      onSuccess: () => {
        refetch();
        customAlert(
          "금형재고 삭제에 성공하였습니다.",
          "삭제 성공",
          "green"
        );
      },
      onError: (error) => {
        customAlert(handleErrorResponse({ error, errorContext: "금형재고 코드" }), "금형재고 삭제 실패", "red");
      }
    }
  );

  const columns: readonly Column<MoldStocksGet200ResponseRowsInner>[] = useMemo(() => [
    {
      ...SelectColumn,
      resizable: true,
    },
    {
      key: "code",
      name: "금형 재고 코드",
      sortable: true,
      resizable: true,
      formatter: (formatterProps) => {
        return (
          <LinkAnchor onClick={() => openModal(<MoldStockDetailForm moldStockCode={formatterProps?.row.code} />, null, "")}>{formatterProps.row.code}</LinkAnchor>
        );
      },
    },
    {
      key: "moldCode",
      name: "금형코드",
      sortable: true,
      resizable: true,
      formatter: (formatterProps) => {
        return (
          <LinkAnchor onClick={() => openModal(<MoldDetailForm moldCode={formatterProps.row?.moldCode} />, null, "")}>{formatterProps.row.moldCode}</LinkAnchor>
        );
      },
    },
    {
      key: "limitCounter",
      name: "한계 타수",
      sortable: true,
      resizable: true,
      formatter: (formatterProps) => {
        return (
          <Flex justify="flex-end">{formatterProps.row.limitCounter}</Flex>
        )
      }
    },
    {
      key: "inspectCounter",
      name: "점검 타수",
      sortable: true,
      resizable: true,
      formatter: (formatterProps) => {
        return (
          <Flex justify="flex-end">{formatterProps.row.inspectCounter}</Flex>
        )
      }
    },
    {
      key: "currentCounter",
      name: "현재 타수",
      sortable: true,
      resizable: true,
      formatter: (formatterProps) => {
        return (
          <Flex justify="flex-end">{formatterProps.row.currentCounter}</Flex>
        )
      }
    },
    {
      key: "options",
      name: "액션",
      sortable: true,
      resizable: true,
      formatter: (formatterProps) => {
        const updateMoldStockActionView = () => {
          openModal(
            <MoldStockUpdateForm formatterProps={formatterProps?.row} />,
            null,
            "금형 재고 수정",
            true
          );
        };

        const deleteMoldStockAction = () => {
          openModal(
            <ConfirmForm
              message="선택한 행을 삭제하시겠습니까?"
              yesCallback={() => deleteMutate({ moldStockId: formatterProps?.row.id as number })}
              noCallback={() => { }}
              commonCallback={() => closeModal(null)}
            />,
            null,
            "금형 재고 삭제",
          )
        }

        return (
          <Flex w="100%" h="100%" justify="center" align="center" gap="md">
            <ActionIcon
              variant="subtle"
              onClick={updateMoldStockActionView}
              color="yellow"
            >
              <IconEdit />
            </ActionIcon>
            <ActionIcon
              variant="subtle"
              onClick={() => deleteMoldStockAction()}
              color="red"
            >
              <IconTrash />
            </ActionIcon>
          </Flex>
        );
      },
    },
  ], [closeModal, deleteMutate, openModal]);

  const handleDelete = () => {
    if ([...selectedRows].length === 0) {
      customAlert("삭제할 금형 재고를 선택해주세요.", "삭제 실패", "red");
      return;
    }

    openModal(
      <ConfirmForm
        message="선택한 행을 삭제하시겠습니까?"
        yesCallback={() => {
          [...selectedRows].forEach((row) => {
            deleteMutate({ moldStockId: row });
          });

          selectedRows.clear();
        }
        }
        noCallback={() => { }}
        commonCallback={() => closeModal(null)}
      />,
      null,
      "금형 재고 삭제",
    )
  }

  return (
    <Flex w="100%" h="100%" gap="xs" direction="column">
      <Flex p="sm" justify="space-between" rowGap="md">
        <ActionHeader createFn={() => openModal(<MoldStockCreateForm />, null, "금형 재고 추가", true)} createDataTitle="금형 재고" deleteFn={() => handleDelete()} />
      </Flex>
      <Flex bg="white" px="sm" direction="column" gap="xs">
        <Text fz="xl" fw={600}>금형 재고 리스트</Text>
        <Text fz="xs">우리 회사에서 다루는 금형 재고에 대해 조회합니다.</Text>
      </Flex>
      <Flex justify="space-between" align="flex-start" px="sm">
        <Flex wrap="wrap" styles={flexGrowable}>
          <Flex gap="xs" align="center" h="100%" wrap="wrap" direction="row">
            <Tooltip label={"사용자 정의 필터입니다. \"필터 추가\" 또는 \"정렬 추가\" 버튼을 클릭하여 조건을 입력합니다."}>
              <Avatar color="blue" variant="outline">
                <IconFilterPlus />
              </Avatar>
            </Tooltip>
            <Flex direction="column" gap="xs" wrap="wrap">
              <Filter />
              <Sorter />
            </Flex>
          </Flex>
        </Flex>
        <Flex styles={flexStable}>
          <SearchBox />
        </Flex>
      </Flex>
      <Flex w="100%" h="100%" p="sm" justify="space-between" gap="md" direction="column">
        <StandardLayoutTableWrapper>
          <ListTable<MoldStocksGet200Response, MoldStocksGet200ResponseRowsInner> columns={columns} />
        </StandardLayoutTableWrapper>
        <PaginationBox<MoldStocksGet200Response> />
      </Flex>
    </Flex>
  );
};
